import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { Link } from 'react-router-dom';
import { useEffect, useState } from "react";

import { cn } from "../lib/utils";

const NAV_ITEMS = {
  frameworks: {
    title: "Frameworks",
    sections: [
      {
        title: "Indian Regulations",
        items: [
          { title: "RBI Compliance", href: "/frameworks/india/rbi" },
          { title: "DPDPA", href: "/frameworks/india/dpdpa" },
          { title: "CERT-In", href: "/frameworks/india/cert-in" },
          { title: "SEBI CSCRF", href: "/frameworks/india/sebi-cscrf" },
          { title: "IRDAI Guidelines", href: "/frameworks/india/irdai" },
        ],
      },
      {
        title: "International",
        items: [
          { title: "ISO 27001", href: "/frameworks/international/iso-27001" },
          { title: "PCI-DSS", href: "/frameworks/international/pci-dss" },
          { title: "NIST CSF", href: "/frameworks/international/nist-csf" },
          { title: "DORA", href: "/frameworks/international/dora" },
        ],
      },
    ],
    featured: {
      title: "Download RBI Control Catalog",
      subtitle: "120+ controls. Evidence-ready.",
      href: "/frameworks/india/rbi",
    },
  },
};

export function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b border-border-subtle",
        isScrolled
          ? "bg-[rgba(17,24,39,0.7)] backdrop-blur-xl"
          : "bg-transparent",
      )}
    >
      <div
        className={cn(
          "mx-auto flex w-full max-w-[1440px] items-center justify-between px-6 transition-[height]",
          isScrolled ? "h-16" : "h-20",
        )}
      >
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="group inline-flex flex-col items-start gap-1"
          >
            <img 
              src="https://cdn.ivaluetv.com/public/ivalue%20transparent%20white.png" 
              alt="iValue" 
              className="h-8 w-auto"
            />
            <span className="text-xs font-semibold tracking-tight text-text-secondary">RegTech CoE</span>
          </Link>
        </div>

        <nav className="hidden items-center gap-8 lg:flex">
          <NavigationMenu.Root>
            <NavigationMenu.List className="flex items-center gap-6">
              <NavigationMenu.Item>
                <Link
                  to="/"
                  className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary underline-hover"
                >
                  Home
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item className="relative">
                <NavigationMenu.Trigger className="group flex items-center gap-2 text-sm font-medium text-text-secondary transition-colors hover:text-text-primary underline-hover">
                  <span>Frameworks</span>
                  <span className="text-text-muted transition-transform group-data-[state=open]:rotate-180">
                    ▾
                  </span>
                </NavigationMenu.Trigger>
                <NavigationMenu.Content className="absolute left-0 top-full z-50 mt-3 rounded-2xl border border-border-subtle bg-background-tertiary/80 p-5 shadow-glass backdrop-blur-2xl">
                  <div className="grid w-[720px] grid-cols-3 gap-6">
                    {NAV_ITEMS.frameworks.sections.map((section) => (
                      <div key={section.title} className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                          {section.title}
                        </div>
                        <div className="space-y-1">
                          {section.items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              className="block rounded-lg px-2 py-2 text-sm text-text-secondary transition-colors hover:bg-white/5 hover:text-text-primary"
                            >
                              {item.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Link
                      to={NAV_ITEMS.frameworks.featured.href}
                      className="sheen rounded-xl border border-border-subtle bg-white/5 p-4 transition-colors hover:bg-white/10"
                    >
                      <div className="text-sm font-semibold text-text-primary">
                        {NAV_ITEMS.frameworks.featured.title}
                      </div>
                      <div className="mt-1 text-sm text-text-secondary">
                        {NAV_ITEMS.frameworks.featured.subtitle}
                      </div>
                      <div className="mt-3 text-sm font-medium text-[color:var(--link)]">
                        View →
                      </div>
                    </Link>
                  </div>
                </NavigationMenu.Content>
              </NavigationMenu.Item>

              <NavigationMenu.Item>
                <Link
                  to="/ot-security"
                  className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary underline-hover"
                >
                  OT/ICS
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item>
                <Link
                  to="/offerings"
                  className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary underline-hover"
                >
                  Solutions
                </Link>
              </NavigationMenu.Item>

              <NavigationMenu.Item>
                <Link
                  to="/resources"
                  className="text-sm font-medium text-text-secondary transition-colors hover:text-text-primary underline-hover"
                >
                  Resources
                </Link>
              </NavigationMenu.Item>
            </NavigationMenu.List>
          </NavigationMenu.Root>

          <div className="flex items-center gap-3">
            <Link
              to="/contact#contact-form"
              className="focus-ring rounded-xl border border-border-subtle bg-white/5 px-4 py-2 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
            >
              Assessment
            </Link>
            <Link
              to="/contact#contact-form"
              className="focus-ring sheen rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] px-4 py-2 text-sm font-semibold text-white shadow-[0_0_40px_-18px_rgba(59,130,246,0.65)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Contact
            </Link>
          </div>
        </nav>

        <div className="lg:hidden">
          <Link
            to="/contact#contact-form"
            className="rounded-xl bg-white/5 px-4 py-2 text-sm font-medium text-text-primary"
          >
            Menu
          </Link>
        </div>
      </div>
    </header>
  );
}
